import {
  WavingHand as WavingHandIcon,
  Description as DescriptionIcon,
  TaskAlt as TaskAltIcon,
  Insights as InsightsIcon,
  Payments as PaymentsIcon,
  Build as BuildIcon,
  Construction as ConstructionIcon,
  HowToReg as HowToRegIcon,
  AccountTree as AccountTreeIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Group as GroupIcon,
  SwapHoriz as SwapHorizIcon,
} from '@mui/icons-material';

// Each slide can optionally specify `lottiePath` (a path under /public/lottie/*.json).
// If absent, the slide renders the MUI icon as a colored visual.
const homeowner = [
  {
    title: 'Welcome to Congrow',
    body: 'Your contractor uses Congrow to keep every estimate, approval, and update in one place. Here is how it works for you.',
    icon: WavingHandIcon,
    color: '#1976d2',
    lottiePath: '/lottie/welcome.json',
  },
  {
    title: 'Review your estimate',
    body: 'You will get a detailed estimate broken down by room — materials, labor, costs. Everything is itemized before any work begins.',
    icon: DescriptionIcon,
    color: '#7c3aed',
    lottiePath: '/lottie/estimate.json',
    ctaLabel: 'See an example estimate',
  },
  {
    title: 'Approve or reject',
    body: 'Sign to approve when you are ready, or reject with feedback so your contractor can revise. Nothing starts without your sign-off.',
    icon: TaskAltIcon,
    color: '#16a34a',
    lottiePath: '/lottie/approve.json',
  },
  {
    title: 'Track progress',
    body: 'As work proceeds, watch progress and disbursements in real time. Photos, comments, and history live on each line item.',
    icon: InsightsIcon,
    color: '#ea580c',
    lottiePath: '/lottie/progress.json',
  },
  {
    title: 'Make payments',
    body: 'Funds are disbursed as items are completed and approved. You always see what has been paid and what remains.',
    icon: PaymentsIcon,
    color: '#0d47a1',
    lottiePath: '/lottie/payment.json',
  },
];

const contractor = [
  {
    title: 'Welcome to Congrow',
    body: 'Build estimates, manage rooms and items, track approvals, and get paid — all in one place. Here is the quick tour.',
    icon: WavingHandIcon,
    color: '#1976d2',
    lottiePath: '/lottie/welcome.json',
  },
  {
    title: 'Build an estimate',
    body: 'Start a new estimate, assign a homeowner (invite them if needed), and add all the rooms you will be working in right at the start.',
    icon: BuildIcon,
    color: '#7c3aed',
    lottiePath: '/lottie/estimate.json',
    ctaLabel: 'Create a new estimate',
    ctaPath: '/contractor',
  },
  {
    title: 'Manage rooms & items',
    body: 'Add all your rooms up front, then build out line items per room with materials, labor, costs, and photos. Group multiple rooms into a single work area whenever it helps.',
    icon: ConstructionIcon,
    color: '#16a34a',
    lottiePath: null,
  },
  {
    title: 'Track approvals',
    body: 'Watch each item move from pending to approved by the homeowner and admin. Mark work complete to unlock disbursement.',
    icon: HowToRegIcon,
    color: '#ea580c',
    lottiePath: '/lottie/approve.json',
  },
  {
    title: 'Get paid',
    body: 'Disburse funds against completed items, generate invoices, and keep a clear audit trail on every project.',
    icon: PaymentsIcon,
    color: '#0d47a1',
    lottiePath: '/lottie/payment.json',
  },
  {
    title: 'Want to see it with real data?',
    body: "We'll spin up a sample project with rooms, items, and dimensions you can poke at — delete it whenever.",
    icon: ConstructionIcon,
    color: '#16a34a',
    lottiePath: null,
    ctaLabel: 'Create a sample project',
    actionKind: 'seedDemo',
  },
];

const admin = [
  {
    title: 'Welcome to Congrow',
    body: 'You have full visibility across every project. Approve, oversee, and resolve disputes from a single dashboard.',
    icon: WavingHandIcon,
    color: '#1976d2',
    lottiePath: '/lottie/welcome.json',
  },
  {
    title: 'Oversee projects',
    body: 'See every project across every contractor and homeowner. Set project status and step in on approvals when needed.',
    icon: AccountTreeIcon,
    color: '#7c3aed',
    lottiePath: null,
  },
  {
    title: 'Manage users',
    body: 'Invite contractors and homeowners, manage account details, and remove accounts that no longer need access.',
    icon: SupervisorAccountIcon,
    color: '#16a34a',
    lottiePath: null,
  },
  {
    title: 'Switch role view',
    body: 'Use the role switcher to see the app exactly as a contractor or homeowner would. Useful for support and debugging.',
    icon: SwapHorizIcon,
    color: '#ea580c',
    lottiePath: null,
  },
  {
    title: 'Want to see it with real data?',
    body: "We'll spin up a sample project with rooms, items, and dimensions you can poke at — delete it whenever.",
    icon: AccountTreeIcon,
    color: '#16a34a',
    lottiePath: null,
    ctaLabel: 'Create a sample project',
    actionKind: 'seedDemo',
  },
];

export const tutorialContent = { homeowner, contractor, admin };

export const TUTORIAL_ROLES = [
  { key: 'homeowner', label: 'Homeowner', icon: GroupIcon, color: '#16a34a' },
  { key: 'contractor', label: 'Contractor', icon: BuildIcon, color: '#1976d2' },
  { key: 'admin', label: 'Admin', icon: SupervisorAccountIcon, color: '#7c3aed' },
];

export const storageKeyFor = (role) => `congrow_tutorial_seen_${role}`;

export const hasSeenTutorial = (role) => {
  try {
    return Boolean(window.localStorage.getItem(storageKeyFor(role)));
  } catch {
    return false;
  }
};

export const markTutorialSeen = (role) => {
  try {
    window.localStorage.setItem(storageKeyFor(role), '1');
  } catch {
    // ignore
  }
};
